import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import useAuth from '../hooks/useAuth';

const EMPTY_EDIT_FORM = {
    nombre: '',
    nodo_id: '',
    subnet: '',
    prefijo: 24,
    gateway: '',
    ip_inicio: '',
    ip_fin: '',
    activo: true
};

const IpPool = () => {
    const { can, user } = useAuth();
    const [items, setItems] = useState([]);
    const [nodos, setNodos] = useState([]);
    const [mikrotiks, setMikrotiks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [search, setSearch] = useState('');

    // Modal editar
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editForm, setEditForm] = useState(EMPTY_EDIT_FORM);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    // Modal importar desde MikroTik
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [mikrotikSeleccionado, setMikrotikSeleccionado] = useState('');
    const [escaneando, setEscaneando] = useState(false);
    const [redesDisponibles, setRedesDisponibles] = useState(null);
    const [redesSeleccionadas, setRedesSeleccionadas] = useState([]);
    const [importando, setImportando] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [poolRes, nodosRes, mkRes] = await Promise.all([
                api.get('/infraestructura/ip-pool'),
                api.get('/infraestructura/nodos'),
                api.get('/infraestructura/mikrotik')
            ]);
            setItems(poolRes.data);
            setNodos(nodosRes.data);
            setMikrotiks(mkRes.data.filter(m => Number(m.activo)));
        } catch {
            setError('Error cargando datos');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (user) loadData(); }, [loadData, user]);

    const showSuccess = (msg) => {
        setSuccess(msg); setError('');
        setTimeout(() => setSuccess(''), 4000);
    };

    // ── Editar IP Pool existente ──
    const openEdit = (item) => {
        setEditForm({
            nombre: item.nombre || '',
            nodo_id: item.nodo_id || '',
            subnet: item.subnet || '',
            prefijo: item.prefijo || 24,
            gateway: item.gateway || '',
            ip_inicio: item.ip_inicio || '',
            ip_fin: item.ip_fin || '',
            activo: !!Number(item.activo)
        });
        setEditingId(item.id);
        setError('');
        setEditModalOpen(true);
    };

    const handleEditChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`¿Está seguro que desea eliminar el IP Pool "${item.nombre}" (${item.subnet}/${item.prefijo})?\n\nEsta acción no se puede deshacer.`)) return;
        setDeletingId(item.id);
        try {
            await api.delete(`/infraestructura/ip-pool/${item.id}`);
            showSuccess(`IP Pool "${item.nombre}" eliminado correctamente`);
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Error eliminando IP Pool');
        } finally {
            setDeletingId(null);
        }
    };

    const handleEditSubmit = async () => {        setSaving(true);
        setError('');
        try {
            await api.put(`/infraestructura/ip-pool/${editingId}`, editForm);
            showSuccess('IP Pool actualizado correctamente');
            setEditModalOpen(false);
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Error guardando IP Pool');
        } finally {
            setSaving(false);
        }
    };

    // ── Importar desde MikroTik ──
    const openImport = () => {
        setMikrotikSeleccionado('');
        setRedesDisponibles(null);
        setRedesSeleccionadas([]);
        setError('');
        setImportModalOpen(true);
    };

    const handleEscanear = async () => {
        if (!mikrotikSeleccionado) return;
        setEscaneando(true);
        setRedesDisponibles(null);
        setRedesSeleccionadas([]);
        setError('');
        try {
            const { data } = await api.get(`/infraestructura/mikrotik/${mikrotikSeleccionado}/redes-disponibles`);
            setRedesDisponibles(data);
            // Pre-seleccionar todas las redes nuevas
            setRedesSeleccionadas(data.redes.map((_, i) => i));
        } catch (err) {
            setError(err.response?.data?.error || 'Error escaneando MikroTik');
        } finally {
            setEscaneando(false);
        }
    };

    const toggleRed = (index) => {
        setRedesSeleccionadas(prev =>
            prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
        );
    };

    const handleImportar = async () => {
        if (redesSeleccionadas.length === 0) return;
        setImportando(true);
        const redes = redesDisponibles.redes.filter((_, i) => redesSeleccionadas.includes(i));
        try {
            await api.post(`/infraestructura/mikrotik/${mikrotikSeleccionado}/importar-pools`, { redes });
            showSuccess(`${redes.length} IP Pool(s) importados correctamente`);
            setImportModalOpen(false);
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Error importando IP Pools');
        } finally {
            setImportando(false);
        }
    };

    const filtered = items.filter(i =>
        `${i.nombre} ${i.subnet} ${i.nodo_nombre || ''}`.toLowerCase().includes(search.toLowerCase())
    );

    const inputClass = "w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none";
    const labelClass = "block text-sm font-medium text-gray-700 mb-1";

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Subredes IP (IP Pool)</h2>
                {can('inf_ip_pool', 'create') && (
                    <button onClick={openImport} className="text-white text-sm font-semibold px-5 py-2.5 rounded-lg" style={{ backgroundColor: 'var(--color-primary)' }}>
                        + Importar desde MikroTik
                    </button>
                )}
            </div>

            {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-4">{success}</div>}
            {error && !editModalOpen && !importModalOpen && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

            <div className="mb-4">
                <input type="text" placeholder="Buscar subred..." value={search} onChange={e => setSearch(e.target.value)}
                    className="w-full max-w-sm border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none" />
            </div>

            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-400 text-sm">Cargando...</div>
                ) : filtered.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">No se encontraron subredes</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                            <tr>
                                <th className="px-4 py-3 text-left">Nombre</th>
                                <th className="px-4 py-3 text-left">Subnet</th>
                                <th className="px-4 py-3 text-left">Prefijo</th>
                                <th className="px-4 py-3 text-left">IP Inicial</th>
                                <th className="px-4 py-3 text-left">IP Final</th>
                                <th className="px-4 py-3 text-left">Gateway</th>
                                <th className="px-4 py-3 text-left">Nodo</th>
                                {can('inf_ip_pool', 'update') && <th className="px-4 py-3 text-right">Acciones</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filtered.map(item => (
                                <tr key={item.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 font-medium text-gray-800">{item.nombre}</td>
                                    <td className="px-4 py-3 text-gray-600 font-mono">{item.subnet}</td>
                                    <td className="px-4 py-3 text-gray-500">/{item.prefijo}</td>
                                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{item.ip_inicio}</td>
                                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{item.ip_fin}</td>
                                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{item.gateway || '—'}</td>
                                    <td className="px-4 py-3 text-gray-500 text-xs">{item.nodo_nombre || '—'}</td>
                                    {can('inf_ip_pool', 'update') && (
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex gap-2 justify-end">
                                                <button onClick={() => openEdit(item)}
                                                    className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">
                                                    Editar
                                                </button>
                                                <button onClick={() => handleDelete(item)} disabled={deletingId === item.id}
                                                    className="text-xs font-medium px-3 py-1.5 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-60">
                                                    {deletingId === item.id ? '...' : 'Eliminar'}
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal Editar */}
            {editModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">Editar IP Pool</h3>
                            <button onClick={() => setEditModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <div className="p-6 space-y-4">
                            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">{error}</div>}
                            <div>
                                <label className={labelClass}>Nombre</label>
                                <input name="nombre" value={editForm.nombre} onChange={handleEditChange} className={inputClass} />
                            </div>
                            <div>
                                <label className={labelClass}>Nodo</label>
                                <select name="nodo_id" value={editForm.nodo_id} onChange={handleEditChange} className={inputClass}>
                                    <option value="">Sin nodo</option>
                                    {nodos.map(n => <option key={n.id} value={n.id}>{n.nombre}</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Subnet</label>
                                    <input name="subnet" value={editForm.subnet} onChange={handleEditChange} className={inputClass} placeholder="Ej: 192.168.170.0" />
                                </div>
                                <div>
                                    <label className={labelClass}>Prefijo</label>
                                    <input name="prefijo" type="number" min="8" max="32" value={editForm.prefijo} onChange={handleEditChange} className={inputClass} />
                                </div>
                            </div>
                            <div>
                                <label className={labelClass}>Gateway</label>
                                <input name="gateway" value={editForm.gateway} onChange={handleEditChange} className={inputClass} placeholder="Ej: 192.168.170.1" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>IP Inicial (clientes)</label>
                                    <input name="ip_inicio" value={editForm.ip_inicio} onChange={handleEditChange} className={inputClass} placeholder="Ej: 192.168.170.2" />
                                </div>
                                <div>
                                    <label className={labelClass}>IP Final (clientes)</label>
                                    <input name="ip_fin" value={editForm.ip_fin} onChange={handleEditChange} className={inputClass} placeholder="Ej: 192.168.170.254" />
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <input type="checkbox" name="activo" checked={editForm.activo} onChange={handleEditChange} id="activo_pool" />
                                <label htmlFor="activo_pool" className="text-sm text-gray-700">Subred activa</label>
                            </div>
                        </div>
                        <div className="px-6 pb-6 flex gap-3">
                            <button onClick={() => setEditModalOpen(false)} className="flex-1 border border-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg text-sm">Cancelar</button>
                            <button onClick={handleEditSubmit} disabled={saving}
                                className="flex-1 text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60"
                                style={{ backgroundColor: 'var(--color-primary)' }}>
                                {saving ? 'Guardando...' : 'Guardar cambios'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Importar desde MikroTik */}
            {importModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">Importar desde MikroTik</h3>
                            <button onClick={() => setImportModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <div className="p-6 space-y-4">
                            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">{error}</div>}

                            <div>
                                <label className={labelClass}>Seleccionar MikroTik *</label>
                                <select value={mikrotikSeleccionado} onChange={e => { setMikrotikSeleccionado(e.target.value); setRedesDisponibles(null); setRedesSeleccionadas([]); }} className={inputClass}>
                                    <option value="">Seleccionar...</option>
                                    {mikrotiks.map(m => <option key={m.id} value={m.id}>{m.etiqueta} ({m.ip})</option>)}
                                </select>
                            </div>

                            {mikrotikSeleccionado && !redesDisponibles && (
                                <button onClick={handleEscanear} disabled={escaneando}
                                    className="w-full text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60"
                                    style={{ backgroundColor: 'var(--color-primary)' }}>
                                    {escaneando ? '🔍 Escaneando...' : '🔍 Escanear MikroTik'}
                                </button>
                            )}

                            {redesDisponibles && (
                                <div className="space-y-3">
                                    <div className={`rounded-lg px-4 py-3 text-sm border ${
                                        redesDisponibles.redes.length > 0
                                            ? 'bg-green-50 border-green-200 text-green-700'
                                            : 'bg-yellow-50 border-yellow-200 text-yellow-700'
                                    }`}>
                                        <p className="font-semibold">{redesDisponibles.mikrotik}</p>
                                        <p className="text-xs mt-1">{redesDisponibles.mensaje}</p>
                                    </div>

                                    {redesDisponibles.redes.length > 0 && (
                                        <div className="border border-gray-200 rounded-lg p-4">
                                            <p className="text-sm font-semibold text-gray-700 mb-3">Redes nuevas disponibles:</p>
                                            <div className="space-y-2">
                                                {redesDisponibles.redes.map((red, i) => (
                                                    <label key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                                                        <input type="checkbox" checked={redesSeleccionadas.includes(i)} onChange={() => toggleRed(i)} className="w-4 h-4" />
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-800">{red.interface} — {red.subnet}/{red.prefijo}</p>
                                                            <p className="text-xs text-gray-500">Gateway: {red.gateway} | Clientes: {red.ip_inicio.replace(/\.\d+$/, '.2')} — {red.ip_fin}</p>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <button onClick={handleEscanear} disabled={escaneando}
                                        className="w-full border border-gray-300 text-gray-600 font-medium py-2 rounded-lg text-sm">
                                        {escaneando ? 'Escaneando...' : '🔄 Volver a escanear'}
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="px-6 pb-6 flex gap-3">
                            <button onClick={() => setImportModalOpen(false)} className="flex-1 border border-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg text-sm">Cerrar</button>
                            {redesDisponibles?.redes.length > 0 && redesSeleccionadas.length > 0 && (
                                <button onClick={handleImportar} disabled={importando}
                                    className="flex-1 text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60"
                                    style={{ backgroundColor: 'var(--color-primary)' }}>
                                    {importando ? 'Importando...' : `Importar ${redesSeleccionadas.length} red(es)`}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IpPool;
